import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MapPin,
  Briefcase,
  ArrowRight,
  Send,
  Upload,
  CheckCircle2,
  AlertCircle,
  FileText,
  X,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import Seo from "@/components/seo/Seo";
import { useJobs } from "@/data/jobsApi";
import useRecaptcha from "@/hooks/useRecaptcha";
import Honeypot from "@/components/Honeypot";
import { BlockRenderer } from "@/components/blocks/BlockRenderer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Renders the editable intro blocks at the top of /careers. Blocks are edited
// via /admin/site-pages/careers-intro. If the fetch fails we render nothing
// so the page still shows job listings + form below.
function CareersIntroBlocks() {
  const [blocks, setBlocks] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/site-pages/careers-intro`);
        setBlocks(Array.isArray(data?.blocks) ? data.blocks : []);
      } catch { /* silent */ }
    })();
  }, []);
  return (
    <>
      {blocks.map((b) => <BlockRenderer key={b.id} block={b} />)}
    </>
  );
}

const referralSources = [
  "Google",
  "LinkedIn",
  "Indeed",
  "Referral",
  "Company Website",
  "Other",
];

export default function CareersPage() {
  const formRef = useRef(null);
  const { jobs: openings, loading: loadingJobs } = useJobs();
  const [position, setPosition] = useState("General Application");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resumeFile, setResumeFile] = useState(null);
  const [honeypot, setHoneypot] = useState("");
  const recaptcha = useRecaptcha();
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    company: "",
    phone: "",
    referral_source: "",
    other_info: "",
  });

  // Deep-link prefill: /careers?position=<title> pre-selects the role and
  // scrolls to the form (used by the "Apply" CTA on /careers/<slug>).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const presetPosition = params.get("position");
    if (presetPosition) {
      setPosition(presetPosition);
      // Defer scroll a tick so the form ref is mounted.
      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, []);

  const scrollToForm = (jobTitle) => {
    if (jobTitle) setPosition(jobTitle);
    if (formRef.current) formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      setError("Resume must be a PDF file.");
      setResumeFile(null);
      e.target.value = "";
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError("Resume must be 8 MB or smaller.");
      setResumeFile(null);
      e.target.value = "";
      return;
    }
    setError("");
    setResumeFile(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!resumeFile) {
      setError("Please attach your resume (PDF).");
      return;
    }
    setLoading(true);
    try {
      const token = await recaptcha.execute("careers_apply");
      const fd = new FormData();
      fd.append("first_name", formData.first_name);
      fd.append("last_name", formData.last_name);
      fd.append("email", formData.email);
      fd.append("company", formData.company);
      fd.append("phone", formData.phone);
      fd.append("referral_source", formData.referral_source);
      fd.append("position", position);
      fd.append("other_info", formData.other_info);
      fd.append("recaptcha_token", token);
      fd.append("website_url", honeypot);
      fd.append("resume", resumeFile, resumeFile.name);

      await axios.post(`${API}/careers/apply`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSubmitted(true);
      toast.success("Application submitted! We'll be in touch.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Something went wrong. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="careers-page" className="page-transition">
      <Seo
        title="Careers at Antera Software | Join the Promo Industry Software Leader"
        description="Join the Antera team. We're hiring engineers, customer success, sales, and product roles. Submit an application even if you don't see a role that matches."
        keywords="antera careers, software jobs, promo industry jobs, plano tx software careers, remote software jobs"
        path="/careers"
      />
      <Toaster position="top-right" richColors />

      {/* Editable intro blocks (hero + values + anything marketing adds).
          Edit these via /admin/site-pages/careers-intro without touching
          the job listings or application form below. */}
      <CareersIntroBlocks />

      {/* Open Positions */}
      <section id="openings" className="py-14 lg:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 mb-3">Open Positions</h2>
          <p className="text-sm text-slate-500 mb-10">
            Don't see a role that matches? We're <span className="font-semibold text-slate-700">Always On</span> - submit a general application below and we'll reach out as opportunities open up.
          </p>
          <div className="space-y-4">
            {loadingJobs && (
              <div data-testid="jobs-loading" className="text-sm text-slate-500 italic">Loading open positions…</div>
            )}
            {!loadingJobs && openings.length === 0 && (
              <div data-testid="jobs-empty" className="text-sm text-slate-500 italic">
                No open positions right now - but we're <span className="font-semibold">Always On</span>. Submit a general application below.
              </div>
            )}
            {openings.map((job, i) => (
              <motion.div
                key={job.slug}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05, duration: 0.3 }}
                data-testid={`job-card-${job.slug}`}
                className="group border border-slate-200 rounded-lg p-5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
              >
                <div className="min-w-0">
                  <Link
                    to={`/careers/${job.slug}`}
                    className="text-base font-semibold text-slate-900 group-hover:text-sky-600 transition-colors block"
                    data-testid={`job-title-link-${job.slug}`}
                  >
                    {job.title}
                  </Link>
                  <div className="flex flex-wrap items-center gap-3 mt-1.5 text-sm text-slate-500">
                    {job.department && (
                      <span className="flex items-center gap-1">
                        <Briefcase className="h-3.5 w-3.5" /> {job.department}
                      </span>
                    )}
                    {job.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3.5 w-3.5" /> {job.location}
                      </span>
                    )}
                    {job.type && (
                      <span className="bg-sky-50 text-sky-700 text-xs font-medium px-2 py-0.5 rounded-full">
                        {job.type}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Link to={`/careers/${job.slug}`}>
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid={`job-view-${job.slug}`}
                      className="rounded-full text-sm font-medium text-sky-600 hover:bg-sky-50"
                    >
                      View Job
                    </Button>
                  </Link>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={`job-apply-${job.slug}`}
                    onClick={() => scrollToForm(job.title)}
                    className="rounded-full border-slate-300 text-sm font-medium"
                  >
                    Apply <ArrowRight className="ml-1 h-3 w-3" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Always On Application Form */}
      <section ref={formRef} id="apply" className="py-14 lg:py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold tracking-tight text-slate-900">
              {submitted ? "Application Received" : "Apply to Antera"}
            </h2>
            {!submitted && (
              <p className="mt-2 text-sm text-slate-500">
                Applying for <span className="font-semibold text-slate-800">{position}</span>. We review every application - even when we don't have an open role.
              </p>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 lg:p-8">
            {submitted ? (
              <div data-testid="careers-success" className="text-center py-8">
                <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 flex items-center justify-center mb-4">
                  <CheckCircle2 className="h-7 w-7 text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">Thanks for applying!</h3>
                <p className="mt-2 text-sm text-slate-500">
                  We've received your application for <span className="font-semibold">{position}</span>. Our team will review it and reach out if there's a fit.
                </p>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSubmitted(false);
                    setFormData({
                      first_name: "",
                      last_name: "",
                      email: "",
                      company: "",
                      phone: "",
                      referral_source: "",
                      other_info: "",
                    });
                    setResumeFile(null);
                    setHoneypot("");
                  }}
                  className="mt-6 rounded-full border-slate-300"
                >
                  Submit another application
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                {error && (
                  <div data-testid="careers-error" className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2.5">
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name" className="text-sm text-slate-700">First name *</Label>
                    <Input
                      id="first_name"
                      data-testid="careers-first-name"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      required
                      className="mt-1.5 h-11 border-slate-300"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name" className="text-sm text-slate-700">Last name *</Label>
                    <Input
                      id="last_name"
                      data-testid="careers-last-name"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      required
                      className="mt-1.5 h-11 border-slate-300"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email" className="text-sm text-slate-700">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="careers-email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="mt-1.5 h-11 border-slate-300"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-sm text-slate-700">Phone *</Label>
                    <Input
                      id="phone"
                      data-testid="careers-phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      className="mt-1.5 h-11 border-slate-300"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="company" className="text-sm text-slate-700">Current company</Label>
                    <Input
                      id="company"
                      data-testid="careers-company"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="mt-1.5 h-11 border-slate-300"
                    />
                  </div>
                  <div>
                    <Label htmlFor="referral_source" className="text-sm text-slate-700">How did you hear about us? *</Label>
                    <Select
                      value={formData.referral_source}
                      onValueChange={(v) => setFormData({ ...formData, referral_source: v })}
                      required
                    >
                      <SelectTrigger id="referral_source" data-testid="careers-referral" className="mt-1.5 h-11 border-slate-300">
                        <SelectValue placeholder="Select an option" />
                      </SelectTrigger>
                      <SelectContent>
                        {referralSources.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Resume upload */}
                <div>
                  <Label htmlFor="resume" className="text-sm text-slate-700">Resume (PDF, max 8 MB) *</Label>
                  {!resumeFile ? (
                    <label
                      htmlFor="resume"
                      className="mt-1.5 flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 hover:border-sky-400 rounded-lg px-4 py-6 cursor-pointer text-sm text-slate-500 hover:bg-sky-50/30 transition-colors"
                    >
                      <Upload className="h-4 w-4" />
                      Click to upload your resume (PDF only)
                    </label>
                  ) : (
                    <div className="mt-1.5 flex items-center gap-3 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2.5">
                      <FileText className="h-5 w-5 text-sky-600 shrink-0" />
                      <span className="text-sm text-slate-700 flex-1 truncate">{resumeFile.name}</span>
                      <span className="text-xs text-slate-500">{(resumeFile.size / 1024).toFixed(0)} KB</span>
                      <button
                        type="button"
                        onClick={() => setResumeFile(null)}
                        className="text-slate-400 hover:text-red-600"
                        aria-label="Remove file"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                  <input
                    id="resume"
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={handleFileChange}
                    data-testid="careers-resume"
                    className="hidden"
                  />
                </div>

                <div>
                  <Label htmlFor="other_info" className="text-sm text-slate-700">Cover note (optional)</Label>
                  <Textarea
                    id="other_info"
                    data-testid="careers-cover"
                    value={formData.other_info}
                    onChange={(e) => setFormData({ ...formData, other_info: e.target.value })}
                    rows={4}
                    placeholder="Tell us a bit about what you're looking for..."
                    className="mt-1.5 border-slate-300"
                  />
                </div>

                {/* Honeypot - invisible anti-bot field */}
                <Honeypot value={honeypot} onChange={setHoneypot} name="website_url_careers" />

                {/* reCAPTCHA v3 runs invisibly on submit. Show the required
                    Google branding notice per reCAPTCHA T&Cs. */}
                <p className="text-[11px] text-slate-400 leading-relaxed">
                  This site is protected by reCAPTCHA and the Google{" "}
                  <a
                    href="https://policies.google.com/privacy"
                    target="_blank"
                    rel="noreferrer noopener"
                    className="underline hover:text-sky-600"
                  >
                    Privacy Policy
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://policies.google.com/terms"
                    target="_blank"
                    rel="noreferrer noopener"
                    className="underline hover:text-sky-600"
                  >
                    Terms of Service
                  </a>{" "}
                  apply.
                </p>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="careers-submit"
                  className="w-full bg-sky-600 hover:bg-sky-700 text-white rounded-full h-12 text-sm font-semibold"
                >
                  {loading ? "Submitting..." : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Submit Application
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
